import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { fetchMyBookings, cancelBooking, extendBooking, endParking } from '../store/bookingSlice';

function CountdownTimer({ endTime }) {
  const [timeLeft, setTimeLeft] = useState('');
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const calc = () => {
      const diff = new Date(endTime) - new Date();
      if (diff <= 0) { setTimeLeft('Expired'); setIsExpired(true); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${h.toString().padStart(2,'0')}:${m.toString().padStart(2,'0')}:${s.toString().padStart(2,'0')}`);
    };
    calc();
    const t = setInterval(calc, 1000);
    return () => clearInterval(t);
  }, [endTime]);

  return (
    <span style={{
      fontFamily: 'monospace', fontSize: '18px', fontWeight: '800',
      color: isExpired ? '#ef4444' : timeLeft.startsWith('00:') ? '#f59e0b' : '#10b981',
    }}>
      {timeLeft}
    </span>
  );
}

const STATUS_COLORS = {
  active: '#10b981', confirmed: '#6366f1', completed: '#64748b',
  cancelled: '#ef4444', pending: '#f59e0b',
};

export default function DashboardPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector(s => s.auth);
  const { bookings, loading } = useSelector(s => s.booking);
  const [activeTab, setActiveTab] = useState('active');
  const [extendHours, setExtendHours] = useState({});

  useEffect(() => {
    dispatch(fetchMyBookings());
  }, [dispatch]);

  const activeBookings = bookings.filter(b => ['active', 'confirmed'].includes(b.status));
  const pastBookings = bookings.filter(b => ['completed', 'cancelled'].includes(b.status));

  const handleCancel = (bookingId) => {
    if (window.confirm('Cancel this booking?')) {
      dispatch(cancelBooking(bookingId));
    }
  };

  const handleExtend = (bookingId) => {
    const hours = extendHours[bookingId] || 1;
    dispatch(extendBooking({ bookingId, hours }));
  };

  const handleEndParking = (bookingId) => {
    if (window.confirm('End parking session now?')) {
      dispatch(endParking(bookingId));
    }
  };

  const tabStyle = (active) => ({
    padding: '10px 20px', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600',
    background: active ? 'rgba(99,102,241,0.15)' : 'transparent',
    color: active ? '#6366f1' : '#64748b',
    border: active ? '1px solid rgba(99,102,241,0.3)' : '1px solid transparent',
  });

  const stats = [
    { label: 'Total Bookings', value: bookings.length, icon: '📋', color: '#6366f1' },
    { label: 'Active Now', value: activeBookings.length, icon: '🚗', color: '#10b981' },
    { label: 'Total Spent', value: `₹${bookings.reduce((s, b) => s + (b.totalAmount || 0), 0)}`, icon: '💰', color: '#f59e0b' },
    { label: 'Hours Parked', value: bookings.reduce((s, b) => s + (b.duration || 0), 0), icon: '⏱️', color: '#8b5cf6' },
  ];

  return (
    <div style={{ paddingTop: '64px', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '900', marginBottom: '8px' }}>
            Welcome back, {user?.name?.split(' ')[0]} 👋
          </h1>
          <p style={{ color: '#64748b' }}>Manage your parking bookings</p>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
          {stats.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              style={{
                background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '16px', padding: '20px',
              }}
            >
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>{s.icon}</div>
              <div style={{ fontSize: '24px', fontWeight: '900', color: s.color, marginBottom: '4px' }}>{s.value}</div>
              <div style={{ color: '#64748b', fontSize: '13px' }}>{s.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Quick Actions */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '32px', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/map')}
            style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              color: '#fff', border: 'none', borderRadius: '12px',
              padding: '12px 24px', fontSize: '14px', fontWeight: '700', cursor: 'pointer',
            }}
          >
            🗺️ Find Parking
          </button>
          <button
            onClick={() => dispatch(fetchMyBookings())}
            style={{
              background: 'rgba(255,255,255,0.05)', color: '#94a3b8',
              border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px',
              padding: '12px 24px', fontSize: '14px', cursor: 'pointer',
            }}
          >
            🔄 Refresh
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
          <button onClick={() => setActiveTab('active')} style={tabStyle(activeTab === 'active')}>
            Active ({activeBookings.length})
          </button>
          <button onClick={() => setActiveTab('history')} style={tabStyle(activeTab === 'history')}>
            History ({pastBookings.length})
          </button>
        </div>

        {/* Bookings */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>Loading bookings...</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {(activeTab === 'active' ? activeBookings : pastBookings).length === 0 ? (
              <div style={{
                textAlign: 'center', padding: '60px',
                background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '20px', color: '#64748b',
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                  {activeTab === 'active' ? '🚗' : '📋'}
                </div>
                <p style={{ fontSize: '16px', marginBottom: '16px' }}>
                  {activeTab === 'active' ? 'No active bookings' : 'No booking history'}
                </p>
                {activeTab === 'active' && (
                  <button
                    onClick={() => navigate('/map')}
                    style={{
                      background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                      color: '#fff', border: 'none', borderRadius: '10px',
                      padding: '10px 24px', fontSize: '14px', fontWeight: '700', cursor: 'pointer',
                    }}
                  >
                    Find Parking Now
                  </button>
                )}
              </div>
            ) : (
              (activeTab === 'active' ? activeBookings : pastBookings).map((booking) => (
                <BookingCard
                  key={booking._id}
                  booking={booking}
                  isActive={activeTab === 'active'}
                  extendHours={extendHours}
                  setExtendHours={setExtendHours}
                  onCancel={handleCancel}
                  onExtend={handleExtend}
                  onEnd={handleEndParking}
                />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function BookingCard({ booking, isActive, extendHours, setExtendHours, onCancel, onExtend, onEnd }) {
  const [showExtend, setShowExtend] = useState(false);
  const statusColor = STATUS_COLORS[booking.status] || '#64748b';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '20px', padding: '24px', overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <h3 style={{ fontSize: '17px', fontWeight: '800' }}>
              {booking.parking?.name || 'Parking Spot'}
            </h3>
            <span style={{
              fontSize: '11px', fontWeight: '700', color: statusColor,
              background: `${statusColor}20`, borderRadius: '6px', padding: '2px 8px',
              textTransform: 'capitalize',
            }}>
              {booking.status}
            </span>
          </div>
          <p style={{ color: '#64748b', fontSize: '13px' }}>
            📍 {booking.parking?.address || 'Address not available'}
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '22px', fontWeight: '900', color: '#6366f1' }}>₹{booking.totalAmount}</div>
          <div style={{ color: '#64748b', fontSize: '12px' }}>{booking.duration}h booked</div>
        </div>
      </div>

      {/* Booking Details Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '16px' }}>
        {[
          { label: 'Vehicle', value: `${booking.vehicleType} • ${booking.vehicleNumber}` },
          { label: 'Start', value: new Date(booking.startTime).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) },
          { label: 'End', value: new Date(booking.endTime).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) },
          { label: 'Booking ID', value: `#${booking._id?.slice(-6)?.toUpperCase()}` },
        ].map((item, i) => (
          <div key={i} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '10px', padding: '10px' }}>
            <div style={{ color: '#64748b', fontSize: '11px', marginBottom: '3px' }}>{item.label}</div>
            <div style={{ fontWeight: '600', fontSize: '13px' }}>{item.value}</div>
          </div>
        ))}
      </div>

      {/* Countdown for active bookings */}
      {booking.status === 'active' && (
        <div style={{
          background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.2)',
          borderRadius: '12px', padding: '14px', marginBottom: '16px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ color: '#94a3b8', fontSize: '13px' }}>⏱️ Time Remaining</span>
          <CountdownTimer endTime={booking.endTime} />
        </div>
      )}

      {/* Actions */}
      {isActive && (
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {booking.status === 'active' && (
            <>
              <button
                onClick={() => setShowExtend(!showExtend)}
                style={{
                  padding: '9px 18px', background: 'rgba(99,102,241,0.1)',
                  border: '1px solid rgba(99,102,241,0.3)', borderRadius: '10px',
                  color: '#6366f1', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                }}
              >
                ⏰ Extend Time
              </button>
              <button
                onClick={() => onEnd(booking._id)}
                style={{
                  padding: '9px 18px', background: 'rgba(16,185,129,0.1)',
                  border: '1px solid rgba(16,185,129,0.3)', borderRadius: '10px',
                  color: '#10b981', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                }}
              >
                ✓ End Parking
              </button>
            </>
          )}
          {['confirmed', 'pending'].includes(booking.status) && (
            <button
              onClick={() => onCancel(booking._id)}
              style={{
                padding: '9px 18px', background: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.3)', borderRadius: '10px',
                color: '#ef4444', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
              }}
            >
              ✕ Cancel
            </button>
          )}
        </div>
      )}

      {/* Extend Panel */}
      {showExtend && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          style={{
            marginTop: '12px', padding: '16px',
            background: 'rgba(99,102,241,0.05)', border: '1px solid rgba(99,102,241,0.2)',
            borderRadius: '12px',
          }}
        >
          <p style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '12px' }}>Extend by how many hours?</p>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            {[1, 2, 3, 4].map(h => (
              <button
                key={h}
                onClick={() => setExtendHours({ ...extendHours, [booking._id]: h })}
                style={{
                  padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                  background: extendHours[booking._id] === h ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${extendHours[booking._id] === h ? 'rgba(99,102,241,0.5)' : 'rgba(255,255,255,0.1)'}`,
                  color: extendHours[booking._id] === h ? '#6366f1' : '#94a3b8',
                }}
              >
                +{h}h
              </button>
            ))}
            <button
              onClick={() => { onExtend(booking._id); setShowExtend(false); }}
              style={{
                padding: '8px 20px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                border: 'none', borderRadius: '8px', color: '#fff',
                fontSize: '13px', fontWeight: '700', cursor: 'pointer',
              }}
            >
              Confirm Extension
            </button>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}